import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useClientAuth } from "@/context/ClientAuthContext";
import { AuthShell } from "@/pages/Login";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function ClientLogin() {
  const navigate = useNavigate();
  const { setClient } = useClientAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [tempToken, setTempToken] = useState("");
  const [code, setCode] = useState("");

  const submit = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/portal/auth/login", { email, password });
      if (data.requires_2fa) setTempToken(data.temp_token);
      else { setClient(data); navigate("/client/dashboard"); }
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "object" ? detail.message : formatApiError(detail) || err.message);
    } finally { setLoading(false); }
  };

  const submit2fa = async (e) => {
    e.preventDefault(); setError(""); setLoading(true);
    try {
      const { data } = await api.post("/portal/auth/2fa/login", { temp_token: tempToken, code });
      setClient(data); navigate("/client/dashboard");
    } catch (err) { setError(formatApiError(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  if (tempToken) {
    return (
      <AuthShell title="Two-factor authentication" subtitle="Enter the 6-digit code from your authenticator app.">
        <form onSubmit={submit2fa} className="space-y-4" data-testid="client-twofa-form">
          <Input inputMode="numeric" autoFocus required value={code} onChange={(e) => setCode(e.target.value)}
            data-testid="client-twofa-code" placeholder="123456" maxLength={6}
            className="bg-[#121214] border-white/10 text-white h-11 tracking-[0.5em] text-center text-lg" />
          {error && <p className="text-sm text-red-400">{error}</p>}
          <Button type="submit" disabled={loading} data-testid="client-twofa-submit"
            className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
            {loading ? "Verifying…" : "Verify & sign in"}</Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell title="Client Portal" subtitle="Sign in to your private workspace.">
      <form onSubmit={submit} className="space-y-4" data-testid="client-login-form">
        <div><Label className="text-[#D4D4D8] mb-1.5 block">Email</Label>
          <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
            data-testid="client-login-email" placeholder="you@company.com" className="bg-[#121214] border-white/10 text-white h-11" /></div>
        <div>
          <div className="flex items-center justify-between mb-1.5"><Label className="text-[#D4D4D8]">Password</Label>
            <Link to="/client/forgot-password" className="text-xs text-[#D4AF37] hover:underline" data-testid="client-forgot-link">Forgot password?</Link></div>
          <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
            data-testid="client-login-password" placeholder="••••••••" className="bg-[#121214] border-white/10 text-white h-11" /></div>
        {error && <p className="text-sm text-red-400" data-testid="client-login-error">{error}</p>}
        <Button type="submit" disabled={loading} data-testid="client-login-submit"
          className="w-full fv-gold-gradient text-[#0A0A0B] font-semibold h-11 rounded-lg hover:opacity-90">
          {loading ? "Signing in…" : "Sign in"}</Button>
      </form>
      <p className="text-center text-sm text-[#71717A] mt-6">Invited by an advisor? Check your email for an activation link.</p>
    </AuthShell>
  );
}
